import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Building2, Check, ChevronDown, Globe } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * Workspace Switcher (Feb 2026)
 *
 * Top-bar dropdown that lets a user with access to multiple tenants
 * (e.g. TekWissen US + TekWissen India) flip between them without
 * logging out.
 *
 * Hidden when the user only has access to a single workspace.
 *
 * Implementation:
 *  - Loads /api/me/tenants on mount.
 *  - On switch: POST /api/auth/switch-tenant {tenant_id}. Server returns
 *    a fresh JWT scoped to the new tenant. We replace the stored token
 *    and reload the page so all tenant-scoped state refetches.
 */
const TOKEN_KEYS = ['saas_token', 'access_token', 'token'];

function readStoredToken() {
  for (const k of TOKEN_KEYS) {
    const v = typeof window !== 'undefined' && window.localStorage.getItem(k);
    if (v) return { key: k, value: v };
  }
  return null;
}

function writeStoredToken(newValue) {
  // Write into whichever key the app already uses; default to saas_token
  const existing = readStoredToken();
  const key = existing?.key || 'saas_token';
  window.localStorage.setItem(key, newValue);
}

export default function WorkspaceSwitcher({ compact = false }) {
  const [tenants, setTenants] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/me/tenants');
      const data = r?.data || r;
      setTenants(data.tenants || []);
      setActiveId(data.active_tenant_id || null);
    } catch (e) {
      // 401/403 → likely not logged in; silently hide
      console.debug('workspace switcher load failed:', e?.message);
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSwitch = async (tenant) => {
    if (tenant.id === activeId || switching) return;
    setSwitching(true);
    try {
      const r = await api.post('/auth/switch-tenant', { tenant_id: tenant.id });
      const data = r?.data || r;
      if (data?.access_token) {
        writeStoredToken(data.access_token);
        toast.success(`Switched to ${tenant.name}`, { duration: 2200 });
        // Hard reload so all tenant-scoped state refetches against the new context
        setTimeout(() => { window.location.assign('/dashboard'); }, 400);
      } else {
        toast.error('Switch returned no token — please log out and log back in');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Switch failed');
    } finally {
      setSwitching(false);
    }
  };

  // Don't show the switcher if user has access to only 1 workspace
  if (loading || tenants.length <= 1) return null;

  const active = tenants.find(t => t.id === activeId) || tenants[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={compact ? 'sm' : 'default'}
          className="gap-2 max-w-[220px]"
          data-testid="workspace-switcher-trigger"
          disabled={switching}
        >
          <Building2 className="h-4 w-4 text-slate-500 shrink-0" />
          <span className="truncate font-medium">{active?.name || 'Workspace'}</span>
          {active?.region && (
            <Badge variant="outline" className="text-[9px] px-1 py-0 border-slate-200">
              {active.region}
            </Badge>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72" data-testid="workspace-switcher-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
          Your workspaces
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {tenants.map((t) => {
          const isActive = t.id === activeId;
          return (
            <DropdownMenuItem
              key={t.id}
              data-testid={`workspace-switcher-item-${t.slug || t.id}`}
              onClick={() => handleSwitch(t)}
              className={`flex items-start gap-3 cursor-pointer ${isActive ? 'bg-slate-50 dark:bg-slate-900/50' : ''}`}
            >
              <div
                className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 text-white text-sm font-bold"
                style={{ background: t.branding?.primary_color || '#6366f1' }}
              >
                {(t.name || 'W').charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-medium text-sm truncate">{t.name}</p>
                  {t.region && (
                    <Badge variant="outline" className="text-[9px] px-1 py-0">
                      {t.region}
                    </Badge>
                  )}
                  {t.is_primary && (
                    <Badge variant="outline" className="text-[9px] px-1 py-0 bg-blue-50 text-blue-700 border-blue-200">
                      primary
                    </Badge>
                  )}
                </div>
                {t.role && (
                  <p className="text-[10px] text-slate-500">{t.role}</p>
                )}
              </div>
              {isActive && <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-1" />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-[10px] text-slate-400 flex items-center gap-1.5">
          <Globe className="h-3 w-3" />
          You can switch back at any time without logging out
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
